import { sendNotification } from '@srm/ui/lib/notifications';
import type { INotification } from '@srm/ui/notifications';
import { IBackgroundJob, IBackgroundJobStarted } from '@models/backgroundJobs/IBackgroundJob';
import { startBackgroundJob } from './awaitBackgroundJob';
import { backgroundJobKind } from './backgroundJobKinds';
import { BackgroundJobsStore, backgroundJobsStore } from './BackgroundJobsStore';
import { BellFlightOrigin, flyToBell } from './flyToBell';

export const GRID_EXPORT_KIND = 'gridExport';

export const GRID_EXPORT_STARTED_TEXT = 'Готовим файл в фоне. Со страницы можно уйти — о готовности сообщим.';
export const GRID_EXPORT_SUGGESTED_TEXT =
    'Список большой — готовим файл в фоне. Со страницы можно уйти — о готовности сообщим.';
/** Подсказка у колокольчика, когда фон выбран по порогу: почему файл не скачался сразу. */
export const GRID_EXPORT_SUGGESTED_HINT = 'Список большой — файл готовится и появится здесь';

export interface IStartGridExportJobOptions {
    /** Фон выбран по порогу, а не пользователем: сообщение объясняет почему. */
    suggested: boolean;
    /** Кнопка выгрузки: из неё задача улетает в колокольчик. */
    from?: BellFlightOrigin;
    store?: Pick<BackgroundJobsStore, 'track'>;
    notify?: (notification: INotification) => void;
    fly?: typeof flyToBell;
}

/**
 * Экспорт списка фоновой задачей: старт → задача в разделе колокольчика (ход и итог с «Скачать файл» — там); капсула
 * задачи улетает от кнопки в колокольчик, и он первые разы подсказывает, что файл появится там. Колокольчика нет —
 * одна фраза о том, где ждать файл. Итог превью у колокольчика объявит клиент задач, этот модуль его не ждёт: уход со
 * страницы задачу не трогает. `unavailable` — фона нет (404, 400 `runInBackground`): выгружать обычным путём. Прочие
 * отказы — исключением с понятным текстом (`startBackgroundJob`), показывает их таблица.
 */
export const startGridExportJob = async (
    start: () => Promise<IBackgroundJobStarted>,
    {
        suggested,
        from,
        store = backgroundJobsStore,
        notify = sendNotification,
        fly = flyToBell,
    }: IStartGridExportJobOptions,
): Promise<'started' | 'unavailable'> => {
    const started = await startBackgroundJob(start, backgroundJobKind(GRID_EXPORT_KIND).wait);
    if (started.type === 'fallback') return 'unavailable';
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- исходный отказ запроса: таблица разбирает его сама
    if (started.type === 'failed') throw started.error ?? new Error(started.message);
    const { jobId } = started;

    // Снимок «в очереди» сразу: короткая выгрузка может закончиться до первого ответа о задаче, а итог колокольчик
    // объявляет только задаче, которую застал идущей.
    const queued: IBackgroundJob = {
        jobId,
        kind: GRID_EXPORT_KIND,
        status: started.status ?? 'queued',
        stage: 'queue',
        createdAt: new Date().toISOString(),
        hasResultFile: false,
    };
    store.track(queued);
    const landed = fly({ from, kind: GRID_EXPORT_KIND, hint: suggested ? GRID_EXPORT_SUGGESTED_HINT : undefined });
    if (!landed) notify({ message: suggested ? GRID_EXPORT_SUGGESTED_TEXT : GRID_EXPORT_STARTED_TEXT });
    return 'started';
};
